import { useQuery } from '@tanstack/react-query';

import { downloadEmails, getEmails, getEmailsByUserId } from '../../api/messagging/emailApi';
import { OmnichannelMessage } from '../../../types';

const mapEmailsToOmnichannel = (emails: Awaited<ReturnType<typeof getEmails>>['data']): OmnichannelMessage[] => {
    return emails.map(item => ({
        id: String(item.id),
        patientId: item.thread_id || item.email || 'unknown',
        patientName: item.email || 'Unknown',
        channel: 'email',
        direction: item.is_outbound ? 'outbound' : 'inbound',
        timestamp: new Date(item.replied_at || item.created_at || Date.now()),
        content: item.text_body || '',
        subject: item.subject,
        // status: item.reply_status === 'failed' ? 'failed' : 'delivered',
    }));
};

export const useGetEmails = (params?: Partial<Parameters<typeof getEmails>[0]>) => {
    const resolvedParams = {
        page: params?.page ?? 1,
        limit: params?.limit ?? 10,
    };

    return useQuery({
        queryKey: ['emails', resolvedParams],
        queryFn: async () => {
            const response = await getEmails(resolvedParams);
            const mapped = mapEmailsToOmnichannel(response.data);
            return {
                data: mapped,
                pagination: response.pagination,
            };
        },
    });
};

export const useGetEmailsByUserId = (userId?: number) => {
    return useQuery({
        queryKey: ['emails-by-user', userId],
        queryFn: async () => {
            if (typeof userId !== 'number') throw new Error('User id is required');
            const response = await getEmailsByUserId(userId);
            const mapped = mapEmailsToOmnichannel(response.data);
            return {
                data: mapped,
                pagination: response.pagination,
            };
        },
        enabled: typeof userId === 'number',
    });
};

export const useDownloadEmails = (
    params: { 
        user_id: number,
        format: 'json' | 'csv' | 'txt' 
    }) => {
    return useQuery({
        queryKey: ['download-emails', params],
        queryFn: async () => {
            const response = await downloadEmails(params);
            return response;
        },
        enabled: false, // Disable automatic query execution
    });
};

